import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfigService } from '~/core/services/appconfig.service';
import { MsalService } from '@azure/msal-angular';

export interface FeedbackRequestPayload {
    thread_id: string;
    question: string;
    /** Explicit backend record id; takes precedence over question-derived lookup. */
    conversation_id?: string;
    sentiment: 'like' | 'dislike';
    documents_relevant?: 'Yes' | 'Partially' | 'No';
    answer_aligned?: 'Yes' | 'Partially' | 'No';
    response_rating?: number;
    additional_comments?: string;
}

/**
 * Chat Service
 * Handles communication with the chatbot API
 */
@Injectable({
    providedIn: 'root'
})
export class ChatService {
    private apiBaseUrl: string = '';
    private websocketBaseUrl: string = '';
    private readonly ENDPOINT = '/chatbot_query';
    private readonly WS_ENDPOINT = '/ws/chat';
    private socket: WebSocket | null = null;
    private activeThreadId: string = '';
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectAttempts: number = 0;
    private readonly MAX_RECONNECT_ATTEMPTS = 10;
    private readonly HEARTBEAT_INTERVAL_MS = 25000; // 25 s — well under Azure's 4-min idle kill
    private lastConnectArgs: { threadId?: string } = {};
    private socketEventsSubject = new Subject<WebSocketEvent>();
    private isAuthenticated: boolean = false;

    private sanitizeForLog(value: unknown, maxLength: number = 300): string {
        const rawValue = typeof value === 'string'
            ? value
            : (() => {
                try {
                    return JSON.stringify(value);
                } catch {
                    return String(value ?? '');
                }
            })();

        const normalizedValue = rawValue
            // Strip newlines, carriage returns, tabs (log-injection vectors)
            .replace(/[\r\n\t]/g, ' ')
            // Strip all ASCII control characters
            .replace(/[\u0000-\u001F\u007F]/g, '')
            // Strip ANSI escape sequences (e.g. \x1b[31m)
            .replace(/\x1b\[[0-9;]*[a-zA-Z]/g, '')
            // Strip printf-style format specifiers (%s, %d, %i, %o, %f, %c)
            .replace(/%[sdifoc%]/gi, '')
            .trim();

        return normalizedValue.length > maxLength
            ? `${normalizedValue.slice(0, maxLength)}…`
            : normalizedValue;
    }

    constructor(
        private http: HttpClient, 
        private appConfigService: AppConfigService,
        private authService: MsalService
    ) {
        this.loadConfigUrl();
    }

    /**
     * Load API base URL from config.json
     */
    private loadConfigUrl(): void {
        this.apiBaseUrl = this.appConfigService.getConfig('apiUrl');
        this.websocketBaseUrl = this.appConfigService.getConfig('websocketUrl') || '';
    }

    private getWebSocketOriginFromWindow(): string {
        const origin = (window?.location?.origin || '').trim().replace(/\/$/, '');

        if (origin.startsWith('https://')) {
            return origin.replace('https://', 'wss://');
        }

        if (origin.startsWith('http://')) {
            return origin.replace('http://', 'ws://');
        }

        if (origin.startsWith('wss://') || origin.startsWith('ws://')) {
            return origin;
        }

        return '';
    }

    /** Resolves WebSocket base URL from config, converting http→ws and https→wss. */
    private getWebSocketBaseUrl(): string {
        const explicitWebSocketUrl = (this.websocketBaseUrl || '').trim().replace(/\/$/, '');
        if (explicitWebSocketUrl) {
            if (explicitWebSocketUrl.startsWith('https://')) {
                return explicitWebSocketUrl.replace('https://', 'wss://');
            }

            if (explicitWebSocketUrl.startsWith('http://')) {
                return explicitWebSocketUrl.replace('http://', 'ws://');
            }

            return explicitWebSocketUrl;
        }

        const normalizedApiUrl = (this.apiBaseUrl || '').trim().replace(/\/$/, '');

        if (normalizedApiUrl.startsWith('https://')) {
            return normalizedApiUrl.replace('https://', 'wss://');
        }

        if (normalizedApiUrl.startsWith('http://')) {
            return normalizedApiUrl.replace('http://', 'ws://');
        }

        if (normalizedApiUrl.startsWith('wss://') || normalizedApiUrl.startsWith('ws://')) {
            return normalizedApiUrl;
        }

        if (normalizedApiUrl.startsWith('/')) {
            return this.getWebSocketOriginFromWindow();
        }

        return this.getWebSocketOriginFromWindow();
    }

    /**
     * Open WebSocket connection for a thread
     */
    public connectWebSocket(threadId?: string): Observable<WebSocketEvent> {
        const wsBaseUrl = this.getWebSocketBaseUrl();
        if (!wsBaseUrl) {
            this.socketEventsSubject.next({
                type: 'error',
                data: { error: 'WebSocket URL not configured. Check apiUrl in config.' }
            });
            return this.socketEventsSubject.asObservable();
        }

        const resolvedThreadId = (threadId || '').trim() || 'new';

        if (
            this.socket &&
            (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) &&
            this.activeThreadId === resolvedThreadId
        ) {
            return this.socketEventsSubject.asObservable();
        }

        this.disconnectWebSocket();

        this.lastConnectArgs = { threadId };
        this.activeThreadId = resolvedThreadId;
        this.reconnectAttempts = 0;
        this.isAuthenticated = false;
        const wsUrl = `${wsBaseUrl}${this.WS_ENDPOINT}/${encodeURIComponent(resolvedThreadId)}`;
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
            this.socketEventsSubject.next({
                type: 'connected',
                data: { thread_id: resolvedThreadId }
            });
            
            this.sendJwtToken();
        };

        this.socket.onmessage = (event: MessageEvent) => {
            try {
                const parsed = JSON.parse(event.data);
                const logSafeThreadId = this.sanitizeForLog(resolvedThreadId);
                
                // Keepalive ping/pong - swallow without forwarding to app
                if (parsed.type === 'ping') {
                    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                        this.socket.send(JSON.stringify({ type: 'pong' }));
                    }
                    return;
                }
                if (parsed.type === 'pong') { return; }

                if (parsed.type === 'auth_success') {
                    this.isAuthenticated = true;
                    this.reconnectAttempts = 0;
                    this.startHeartbeat();
                } else if (parsed.type === 'auth_failed') {
                    const logSafeError = this.sanitizeForLog(parsed?.data?.error || 'unknown');
                    // Pass user-controlled values as a separate argument (not interpolated)
                    console.error('❌ [WS-CLIENT] Authentication failed', { error: logSafeError, thread_id: logSafeThreadId });
                    this.isAuthenticated = false;
                    this.stopHeartbeat();
                }
                
                this.socketEventsSubject.next(parsed);
            } catch {
                const logSafeThreadId = this.sanitizeForLog(resolvedThreadId);
                // Pass user-controlled values as a separate argument (not interpolated)
                console.error('❌ [WS-CLIENT] Invalid WebSocket message format', { thread_id: logSafeThreadId });
                this.socketEventsSubject.next({
                    type: 'error',
                    data: { error: 'Invalid WebSocket message format from server.' }
                });
            }
        };

        this.socket.onerror = () => {
            this.socketEventsSubject.next({
                type: 'error',
                data: { error: 'WebSocket connection error.' }
            });
        };

        this.socket.onclose = (ev: CloseEvent) => {
            this.isAuthenticated = false;
            this.stopHeartbeat();
            this.socketEventsSubject.next({
                type: 'disconnected',
                data: { thread_id: resolvedThreadId }
            });
            const noReconnectCodes = [1000, 1001, 1008];
            if (!noReconnectCodes.includes(ev.code) && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
                const delayMs = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                this.reconnectAttempts++;
                this.reconnectTimer = setTimeout(() => { this.connectWebSocket(this.lastConnectArgs.threadId); }, delayMs);
            }
        };

        return this.socketEventsSubject.asObservable();
    }

    /** Sends JWT token to backend after WebSocket opens for authentication. */
    private sendJwtToken(): void {
        try {
            const account = this.authService.instance.getActiveAccount();
            if (!account) {
                console.error('❌ [WS-CLIENT-AUTH] No active MSAL account found');
                this.socketEventsSubject.next({
                    type: 'auth_failed',
                    data: { error: 'No active user session found. Please log in again.' }
                });
                this.disconnectWebSocket();
                return;
            }

            const jwtToken = account.idToken;
            if (!jwtToken) {
                console.error('❌ [WS-CLIENT-AUTH] No JWT token found in active account');
                this.socketEventsSubject.next({
                    type: 'auth_failed',
                    data: { error: 'Authentication token not found. Please log in again.' }
                });
                this.disconnectWebSocket();
                return;
            }

            if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
                console.error('❌ [WS-CLIENT-AUTH] WebSocket not open, cannot send JWT token');
                return;
            }

            const authPayload = {
                jwt_token: jwtToken
            };

            this.socket.send(JSON.stringify(authPayload));
        } catch (error) {
            console.error('❌ [WS-CLIENT-AUTH] Error sending JWT token:', error);
            this.socketEventsSubject.next({
                type: 'auth_failed',
                data: { error: 'Failed to send authentication token.' }
            });
            this.disconnectWebSocket();
        }
    }

    /**
     * Send message over active WebSocket connection
     */
    public sendMessageOverWebSocket(
        message: string,
        chatHistory: [string, string][],
        threadId?: string,
        userContext?: { top_k?: number; search_mode?: string; ewo_qna_mode?: boolean; selected_agent?: string },
        drtPayload?: {
            pdf_file_b64?: string;
            image_file_b64?: string;
            pdf_file_name?: string;
            image_file_name?: string;
            verified_process_info?: Record<string, string>;
            scope_parameters?: Record<string, string>;
            drt_parameters_data?: any;
            search_more_ewos?: boolean;
            source_conversation_id?: string;
            top_k?: number;
        }
    ): boolean {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            this.socketEventsSubject.next({
                type: 'error',
                data: { error: 'WebSocket is not connected.' }
            });
            return false;
        }

        const normalizedThreadId = (threadId || '').trim();
        const fallbackThreadId = (this.activeThreadId || '').trim() || 'new';
        const resolvedThreadId = normalizedThreadId || fallbackThreadId;

        const payload: {
            message: string;
            chat_history: [string, string][];
            user_context: { top_k?: number; search_mode?: string; ewo_qna_mode?: boolean; selected_agent?: string };
            thread_id: string;
            pdf_file_b64?: string;
            image_file_b64?: string;
            pdf_file_name?: string;
            image_file_name?: string;
            verified_process_info?: Record<string, string>;
            scope_parameters?: Record<string, string>;
            drt_parameters_data?: any;
            search_more_ewos?: boolean;
            source_conversation_id?: string;
            top_k?: number;
        } = {
            message,
            chat_history: chatHistory,
            user_context: userContext || { top_k: 5, search_mode: 'quick', ewo_qna_mode: false },
            thread_id: resolvedThreadId
        };

        if (drtPayload) {
            if (drtPayload.pdf_file_b64) {
                payload.pdf_file_b64 = drtPayload.pdf_file_b64;
            }
            if (drtPayload.image_file_b64) {
                payload.image_file_b64 = drtPayload.image_file_b64;
            }
            if (drtPayload.pdf_file_name) {
                payload.pdf_file_name = drtPayload.pdf_file_name;
            }
            if (drtPayload.image_file_name) {
                payload.image_file_name = drtPayload.image_file_name;
            }
            if (drtPayload.verified_process_info) {
                payload.verified_process_info = drtPayload.verified_process_info;
            }
            if (drtPayload.scope_parameters) {
                payload.scope_parameters = drtPayload.scope_parameters;
            }
            if (drtPayload.drt_parameters_data) {
                payload.drt_parameters_data = drtPayload.drt_parameters_data;
            }
            if (drtPayload.search_more_ewos) {
                payload.search_more_ewos = true;
            }
            if (drtPayload.source_conversation_id) {
                payload.source_conversation_id = drtPayload.source_conversation_id;
            }
            if (drtPayload.top_k !== undefined && drtPayload.top_k !== null) {
                payload.top_k = drtPayload.top_k;
            }
        }

        this.socket.send(JSON.stringify(payload));
        return true;
    }

    /**
     * Close active WebSocket connection
     */
    public disconnectWebSocket(): void {
        this.stopHeartbeat();
        if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
    }

    private startHeartbeat(): void {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type: 'ping' }));
            }
        }, this.HEARTBEAT_INTERVAL_MS);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    }

    /** Sends initial message to chatbot and returns an Observable with thread_id and response. */
    public sendFirstMessage(message: string): Observable<ChatbotResponse> {
        const payload = {
            message: message
        };

        return this.http.post<ChatbotResponse>(
            `${this.apiBaseUrl}${this.ENDPOINT}`,
            payload
        );
    }

    /** Sends a followup message in an existing conversation thread. */
    public sendFollowupMessage(
        message: string,
        threadId: string,
        chatHistory: [string, string][],
        userContext?: { top_k?: number; search_mode?: string }
    ): Observable<ChatbotResponse> {
        const payload = {
            message: message,
            thread_id: threadId,
            chat_history: chatHistory,
            user_context: userContext || { top_k: 5, search_mode: 'quick' }
        };

        return this.http.post<ChatbotResponse>(
            `${this.apiBaseUrl}${this.ENDPOINT}`,
            payload
        );
    }

    /**
     * Fetch 3 follow-up question suggestions.
     */
    public getFollowUpSuggestions(
        chatHistory: [string, string][],
        agentUsed: string | null,
        threadId: string | null
    ): Observable<string[]> {
        const account = this.authService.instance.getActiveAccount();
        const jwtToken = account?.idToken;

        if (!jwtToken) {
            return of([]);
        }

        const headers = new HttpHeaders({ Authorization: `Bearer ${jwtToken}` });
        const body: Record<string, any> = { chat_history: chatHistory };
        if (agentUsed) { body['agent_used'] = agentUsed; }
        if (threadId)  { body['thread_id']  = threadId; }

        return this.http.post<{ suggestions?: string[] }>(
            `${this.apiBaseUrl}/followup/suggestions`,
            body,
            { headers }
        ).pipe(
            map(res => res?.suggestions ?? []),
            catchError(() => of([]))
        );
    }

    /**
     * Submit structured feedback for a chatbot turn.
     * Payload aligns with backend /feedback contract.
     */
    public submitFeedback(payload: FeedbackRequestPayload): Observable<any> {
        const account = this.authService.instance.getActiveAccount();
        const jwtToken = account?.idToken;

        if (!jwtToken) {
            return throwError(() => new Error('JWT token not found. Please log in again.'));
        }

        const headers = new HttpHeaders({
            Authorization: `Bearer ${jwtToken}`
        });

        return this.http.post(
            `${this.apiBaseUrl}/feedback`,
            payload,
            { headers }
        );
    }
}

export interface ChatbotResponse {
    response: string;
    agent: string;
    thread_id: string;
    routing: {
        primary_agent: string;
        secondary_agents?: string[];
        confidence: number;
        reasoning: string;
    };
    evaluation?: any;
    timestamp: string;
    citations?: { [key: string]: string };
}

export interface DrtProgressStep {
    step: string;
    label: string;
    status: 'start' | 'progress' | 'done' | 'error';
    detail?: string;
}

export interface WebSocketEvent {
    type: 'connected' | 'auth_success' | 'auth_failed' | 'routing' | 'agent_processing' | 'agent_result' | 'response' | 'conversation_saved' | 'complete' | 'drt_parameters' | 'drt_result' | 'drt_progress' | 'error' | 'disconnected';
    data: any;
}
